import React from "react";
import { cn } from "@repo/ui/utils.ts";
import { PortableText, type PortableTextReactComponents } from "next-sanity";
import {
  asPortableText,
  hardBreakHandler,
  onMissingPortableTextComponent,
  proseBaseClasses,
} from "./shared";

/**
 * Dedicated component for the `richTextBodyCopySimple` schema.
 *
 * Schema permits `normal` blocks; `strong` and `em` decorators. No lists, no
 * annotations. The `as` prop picks a typography preset and, for the heading
 * modes, re-tags the normal block as a semantic heading element.
 *
 * For body modes the wrapper carries the typography class — paragraphs
 * inherit font-size from their wrapper. For heading modes the typography is
 * applied directly to the `<h2>` / `<h1>` because the `prose` plugin sets
 * its own font-size on heading elements, which would otherwise win.
 */
export type RichTextBodyCopySimpleAs =
  | "body"
  | "body-tablet"
  | "body-lg"
  | "body-xl"
  | "body-sm"
  | "body-md-sm"
  | "body-md-flat"
  | "editorial-lead"
  | "heading"
  | "heading-serif"
  | "heading-section"
  | "heading-h6"
  | "heading-display";

const wrapperClasses: Record<RichTextBodyCopySimpleAs, string> = {
  body: "typography-body-sm-regular desktop:typography-body-md-regular",
  "body-tablet": "typography-body-sm-regular tablet:typography-body-md-regular",
  "body-lg": "typography-body-md-regular desktop:typography-body-lg-regular",
  "body-xl": "typography-body-md-regular desktop:typography-body-xl-regular",
  "body-sm": "typography-body-xs-regular desktop:typography-body-sm-regular",
  "body-md-sm": "typography-body-md-regular desktop:typography-body-sm-regular",
  "body-md-flat": "typography-body-md-regular",
  // Editorial serif sub-lead (Season Mix, H4) — used under big headlines.
  "editorial-lead":
    "typography-mobile-editorial-h4 desktop:typography-desktop-editorial-h4",
  heading: "",
  "heading-serif": "",
  "heading-section": "",
  "heading-h6": "",
  "heading-display": "",
};

const HEADING_H5_CLASS =
  "typography-mobile-heading-h5 desktop:typography-desktop-heading-h5 m-0 text-inherit";
// Editorial statements / quotes (Season Mix, H3).
const HEADING_SERIF_CLASS =
  "typography-mobile-editorial-h3 desktop:typography-desktop-editorial-h3 m-0 text-inherit";
// Mid Yellix section heading (between H6 and H2) — long-form / article sections.
const HEADING_SECTION_CLASS =
  "typography-mobile-heading-section desktop:typography-desktop-heading-section m-0 text-inherit";
const HEADING_H6_CLASS =
  "typography-mobile-heading-h6 desktop:typography-desktop-heading-h6 m-0 text-inherit";
const HEADING_DISPLAY_CLASS =
  "typography-mobile-heading-h1-voyage desktop:typography-desktop-heading-h1-voyage m-0 text-inherit";

function NormalAsH2H5(props: React.PropsWithChildren) {
  return <h2 className={HEADING_H5_CLASS}>{props.children}</h2>;
}
function NormalAsH2Serif(props: React.PropsWithChildren) {
  return <h2 className={HEADING_SERIF_CLASS}>{props.children}</h2>;
}
function NormalAsH2Section(props: React.PropsWithChildren) {
  return <h2 className={HEADING_SECTION_CLASS}>{props.children}</h2>;
}
function NormalAsH2H6(props: React.PropsWithChildren) {
  return <h2 className={HEADING_H6_CLASS}>{props.children}</h2>;
}
function NormalAsH1(props: React.PropsWithChildren) {
  return <h1 className={HEADING_DISPLAY_CLASS}>{props.children}</h1>;
}

function getComponents(
  as: RichTextBodyCopySimpleAs,
): Partial<PortableTextReactComponents> {
  const block: PortableTextReactComponents["block"] = {};
  if (as === "heading") block.normal = NormalAsH2H5;
  else if (as === "heading-serif") block.normal = NormalAsH2Serif;
  else if (as === "heading-section") block.normal = NormalAsH2Section;
  else if (as === "heading-h6") block.normal = NormalAsH2H6;
  else if (as === "heading-display") block.normal = NormalAsH1;

  return {
    block,
    hardBreak: hardBreakHandler,
  };
}

interface RichTextBodyCopySimpleProps<T = unknown> {
  richText?: T | null;
  className?: string;
  as?: RichTextBodyCopySimpleAs;
  compact?: boolean;
}

export function RichTextBodyCopySimple<T = unknown>({
  richText,
  className,
  as = "body",
  compact,
}: RichTextBodyCopySimpleProps<T>) {
  if (!richText) return null;
  return (
    <div
      className={cn(
        proseBaseClasses,
        wrapperClasses[as],
        compact && "[&>p]:m-0",
        className,
      )}
    >
      <PortableText
        value={asPortableText(richText)}
        components={getComponents(as)}
        onMissingComponent={onMissingPortableTextComponent}
      />
    </div>
  );
}
